import { RefObject, useMemo } from "react";
import { useWindowSize } from "usehooks-ts";
import { useBreakpoint } from "./useBreakpoint";
import { useRect } from "./useRect";

/**
 * Configuration options for the useMaxPaneWidth hook
 */
export interface UseMaxPaneWidthOptions {
  /**
   * Whether the left panel is currently open
   */
  isLeftPanelOpen: boolean;
  /**
   * Whether the right panel is currently open
   */
  isRightPanelOpen: boolean;
  /**
   * Optional ref to a container element. If provided, calculations will be based
   * on the container's width. If not provided, calculations will use window width.
   */
  containerRef?: RefObject<HTMLElement>;
}

/**
 * Result of the useMaxPaneWidth hook
 */
export interface MaxPaneWidth {
  /**
   * Maximum width for the left pane in pixels
   */
  leftPane: number;
  /**
   * Maximum width for the right pane in pixels
   */
  rightPane: number;
}

/**
 * Hook to dynamically calculate maximum pane widths based on available space.
 * Prevents ResizablePanel components from expanding beyond available space.
 *
 * @example
 * ```tsx
 * // For global layouts (using window width)
 * const maxPaneWidth = useMaxPaneWidth({
 *   isLeftPanelOpen: !!leftPanePath,
 *   isRightPanelOpen: !!rightPanePath
 * });
 *
 * // For page-specific layouts (using container ref)
 * const containerRef = useRef<HTMLDivElement>(null);
 * const maxPaneWidth = useMaxPaneWidth({
 *   isLeftPanelOpen: false,
 *   isRightPanelOpen: !!selectedItem,
 *   containerRef
 * });
 * ```
 *
 * @param options - Configuration options for calculating max pane widths
 * @returns Object containing maximum widths for left and right panes
 */
export function useMaxPaneWidth({
  isLeftPanelOpen,
  isRightPanelOpen,
  containerRef,
}: UseMaxPaneWidthOptions): MaxPaneWidth {
  const windowSize = useWindowSize();
  const containerRect = useRect(containerRef || { current: null });
  const lg = useBreakpoint("lg");

  // Determine available width based on whether a container ref was provided
  const availableWidth = containerRef?.current
    ? containerRect.width
    : windowSize.width;

  // Memoize the calculation to prevent unnecessary recalculations
  return useMemo(() => {
    // Constants for layout calculations
    const COLLAPSED_SIDEBAR_WIDTH = 80; // Approximate width of collapsed sidebar
    const MIN_MAIN_WIDTH = 800; // Minimum width for main content area
    const MIN_PANE_WIDTH = 500; // Minimum pane width when open
    const MAX_SINGLE_PANE_PERCENT = 0.75; // Maximum width for a single pane as percentage

    // On mobile, panes don't show simultaneously with main content
    if (!lg) {
      return {
        leftPane: availableWidth - COLLAPSED_SIDEBAR_WIDTH,
        rightPane: availableWidth - COLLAPSED_SIDEBAR_WIDTH,
      };
    }

    // Calculate available width for panes
    const totalAvailableWidth = availableWidth - COLLAPSED_SIDEBAR_WIDTH;
    const availableForPanes = totalAvailableWidth - MIN_MAIN_WIDTH;

    // If not enough space, return minimal widths
    if (availableForPanes < MIN_PANE_WIDTH) {
      return {
        leftPane: MIN_PANE_WIDTH,
        rightPane: MIN_PANE_WIDTH,
      };
    }

    // Calculate max width for each pane based on which panes are open
    const bothPanesOpen = isLeftPanelOpen && isRightPanelOpen;

    if (bothPanesOpen) {
      // When both panes are open, they share the available space
      const maxSharedWidth = Math.min(
        availableForPanes / 2,
        availableWidth * MAX_SINGLE_PANE_PERCENT,
        totalAvailableWidth * 0.4, // Don't let each pane take more than 40% of total width when both open
      );
      return {
        leftPane: Math.max(MIN_PANE_WIDTH, maxSharedWidth),
        rightPane: Math.max(MIN_PANE_WIDTH, maxSharedWidth),
      };
    } else {
      // When only one pane is open OR both are closed, use the single pane maximum
      // This ensures maxWidth doesn't change when panels close, preventing width resets
      const maxSingleWidth = Math.min(
        availableForPanes,
        availableWidth * MAX_SINGLE_PANE_PERCENT,
        totalAvailableWidth * MAX_SINGLE_PANE_PERCENT, // Don't let a single pane take more than 75% of total width
      );
      return {
        leftPane: Math.max(MIN_PANE_WIDTH, maxSingleWidth),
        rightPane: Math.max(MIN_PANE_WIDTH, maxSingleWidth),
      };
    }
  }, [availableWidth, isLeftPanelOpen, isRightPanelOpen, lg]);
}
